import {
  ACCESS,
  ACCESS_DASHBOARDS_V4,
  ACCESS_HEALTH_INDICATOR,
  ACCESS_NAVIGATION,
  ACCESS_SRM,
  ACCESS_TQL,
  DASHBOARDS,
  SETTINGS,
  SETTINGS_ALERT_MANAGER,
  SETTINGS_ATTRIBUTES,
  SETTINGS_CI_TYPES,
  SETTINGS_COMPONENTS,
  SETTINGS_CONTENT_PACK,
  SETTINGS_ENRICHMENT,
  SETTINGS_ENRICHMENT_LOGS,
  SETTINGS_GENERAL,
  SETTINGS_GLOBAL,
  SETTINGS_HEALTH_INDICATORS,
  SETTINGS_LDAP,
  SETTINGS_LICENSE,
  SETTINGS_MONITORING_EXPORTERS,
  SETTINGS_MONITORING_JOBS,
  SETTINGS_MONITORING_PATH,
  SETTINGS_MONITORING_TEMPLATES,
  SETTINGS_MY_RECEIVERS,
  SETTINGS_NOTIFICATIONS,
  SETTINGS_NOTIFICATIONS_TEMPLATES,
  SETTINGS_OIDC,
  SETTINGS_PLUGINS,
  SETTINGS_SENDERS,
  SETTINGS_SRM,
  SETTINGS_SYSTEM,
  SETTINGS_TIME_INTERVAL,
  SETTINGS_USER_ATTRIBUTES,
  SETTINGS_VM_AGENTS,
  SETTINGS_VM_ALERTS,
  SRM,
  USERS,
  USERS_AUDIT_LOGS,
  USERS_REGISTRY,
  USERS_SESSIONS,
} from '@/shared/config/routes';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';

import type { AppSearchAction } from '../types';

export type SearchAliasMap = Record<string, string>;

/** Hardcoded stand-in until aliases are fetched. */
export const PLACEHOLDER_ALIASES: SearchAliasMap = {
  [DASHBOARDS]: 'db',
  [SRM]: 'srm',
  [SETTINGS_MY_RECEIVERS]: 'recv',
  [ACCESS]: 'acl',
  [ACCESS_SRM]: 'asrm',
  [ACCESS_HEALTH_INDICATOR]: 'ahi',
  [ACCESS_NAVIGATION]: 'anav',
  [ACCESS_DASHBOARDS_V4]: 'adb',
  [ACCESS_TQL]: 'atql',
  [USERS]: 'u',
  [USERS_REGISTRY]: 'usr',
  [USERS_SESSIONS]: 'ses',
  [USERS_AUDIT_LOGS]: 'aud',
  [SETTINGS]: 's',
  [SETTINGS_SRM]: 'ssrm',
  [SETTINGS_CI_TYPES]: 'cit',
  [SETTINGS_ENRICHMENT]: 'enr',
  [SETTINGS_ENRICHMENT_LOGS]: 'enrl',
  [SETTINGS_ATTRIBUTES]: 'attr',
  [SETTINGS_MONITORING_PATH]: 'mon',
  [SETTINGS_HEALTH_INDICATORS]: 'hi',
  [SETTINGS_MONITORING_EXPORTERS]: 'exp',
  [SETTINGS_MONITORING_JOBS]: 'jobs',
  [SETTINGS_MONITORING_TEMPLATES]: 'mt',
  [SETTINGS_VM_AGENTS]: 'vma',
  [SETTINGS_VM_ALERTS]: 'vml',
  [SETTINGS_USER_ATTRIBUTES]: 'uattr',
  [SETTINGS_LDAP]: 'ldap',
  [SETTINGS_OIDC]: 'oidc',
  [SETTINGS_SYSTEM]: 'sys',
  [SETTINGS_CONTENT_PACK]: 'cp',
  [SETTINGS_COMPONENTS]: 'cmp',
  [SETTINGS_PLUGINS]: 'plg',
  [SETTINGS_GLOBAL]: 'glob',
  [SETTINGS_NOTIFICATIONS]: 'ntf',
  [SETTINGS_TIME_INTERVAL]: 'ti',
  [SETTINGS_ALERT_MANAGER]: 'am',
  [SETTINGS_GENERAL]: 'ngen',
  [SETTINGS_SENDERS]: 'snd',
  [SETTINGS_NOTIFICATIONS_TEMPLATES]: 'tpl',
  [SETTINGS_LICENSE]: 'lic',
};

export const applyAliasesToTree = (
  nodes: AppSearchAction[],
  aliasMap: SearchAliasMap = PLACEHOLDER_ALIASES,
): AppSearchAction[] =>
  treeMap(nodes, 'children', (node) => {
    const alias = node.to ? aliasMap[node.to] : undefined;
    if (!alias) {
      return node;
    }

    return {
      ...node,
      alias,
    };
  });

export const getSearchRank = (action: AppSearchAction, filter: string) => {
  const query = filter.trim().toLowerCase();
  if (!query) {
    return 3;
  }

  const alias = action.alias?.trim().toLowerCase();
  if (alias === query) {
    return 0;
  }
  if (alias?.startsWith(query)) {
    return 1;
  }

  const label = action.label?.trim().toLowerCase();
  if (label?.startsWith(query)) {
    return 2;
  }

  return 3;
};

export const sortTreeByAlias = (
  nodes: AppSearchAction[],
  filter: string,
): AppSearchAction[] => {
  if (!filter.trim()) {
    return nodes;
  }

  return [...nodes]
    .sort(
      (left, right) =>
        getSearchRank(left, filter) - getSearchRank(right, filter),
    )
    .map((node) => ({
      ...node,
      children: node.children
        ? sortTreeByAlias(node.children, filter)
        : node.children,
    }));
};

export const sortActionsByAlias = <T extends AppSearchAction>(
  actions: T[],
  filter: string,
): T[] => {
  if (!filter.trim()) {
    return actions;
  }

  return [...actions].sort(
    (left, right) => getSearchRank(left, filter) - getSearchRank(right, filter),
  );
};